import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/atoms/dialog';
import { Sheet, SheetContent, SheetTitle } from '@/components/atoms/sheet';
import {
  ContactFormPanel,
  type ContactFormIrisMarks,
  type ContactCreatePrefill,
} from './ContactFormPanel';
import { normalizeContact } from '@/hooks/contacts/useContacts';
import { apiGet, operationsClient } from '@/lib/api';
import type { Contact } from '@/types/contact';

interface ContactCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  presentation?: 'dialog' | 'drawer';
  initialValues?: ContactCreatePrefill;
  autofillValues?: ContactCreatePrefill;
  irisMarks?: ContactFormIrisMarks;
  onSelectContact: (contact: Contact) => void;
}

/** The Contacts form, with its result selected back into the calling document. */
export function ContactCreateDialog({
  open,
  onOpenChange,
  presentation = 'dialog',
  ...props
}: ContactCreateDialogProps) {
  const body = open ? (
    <ContactCreateDialogBody
      {...props}
      stackHeaderOnMobile={presentation === 'drawer'}
      onClose={() => onOpenChange(false)}
    />
  ) : null;

  if (presentation === 'drawer') {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl"
          showCloseButton={false}
          aria-describedby={undefined}
        >
          <SheetTitle className="sr-only">New customer</SheetTitle>
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[min(900px,calc(100dvh-2rem))] flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl"
        showCloseButton={false}
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">New customer</DialogTitle>
        {body}
      </DialogContent>
    </Dialog>
  );
}

function ContactCreateDialogBody({
  initialValues,
  autofillValues,
  irisMarks,
  onSelectContact,
  onClose,
  stackHeaderOnMobile,
}: Omit<ContactCreateDialogProps, 'open' | 'onOpenChange' | 'presentation'> & {
  onClose: () => void;
  stackHeaderOnMobile: boolean;
}) {
  const [pendingContactId, setPendingContactId] = useState<string>();
  const [loadFailed, setLoadFailed] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  async function selectContact(contactId: string) {
    if (!mounted.current) return;
    setPendingContactId(contactId);
    setLoadFailed(false);
    try {
      // POST /contacts doesn't include the saved address rows. Load the actual
      // IDs, country, billing/service flags and tax settings before scheduling.
      const raw = await apiGet<Record<string, unknown>>(operationsClient, `/contacts/${contactId}`);
      if (!mounted.current) return;
      onSelectContact(normalizeContact(raw));
      onClose();
    } catch {
      // Keep the saved ID and retry only this read, never POST a duplicate.
      if (mounted.current) setLoadFailed(true);
    }
  }

  if (pendingContactId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6">
        {loadFailed ? (
          <>
            <p role="alert">Couldn't load the contact. Retry to select it for this job.</p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="button" onClick={() => void selectContact(pendingContactId)}>
                Retry
              </Button>
            </div>
          </>
        ) : (
          <p role="status" className="flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" /> Loading contact…
          </p>
        )}
      </div>
    );
  }

  return (
    <ContactFormPanel
      mode="create"
      initialValues={initialValues}
      autofillValues={autofillValues}
      irisMarks={irisMarks}
      allowAddAnother={false}
      stackHeaderOnMobile={stackHeaderOnMobile}
      onCancel={onClose}
      onSaved={(contact) => void selectContact(contact.id)}
      onOpenExisting={(contactId) => void selectContact(contactId)}
    />
  );
}
